<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.owner_id"
          placeholder="Select Owner Name"
          class="px-2"
          :options="ownerOptions"
        />
      </vs-col>
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.route_name"
          placeholder="Select Route Name"
          class="px-2"
          :options="routeOptions"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.month"
          :format="'MMM'"
          type="month"
          value-type="M"
          placeholder="Month"
          class="px-2"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.year"
          :format="'YYYY'"
          type="year"
          value-type="YYYY"
          placeholder="Year"
          class="px-2"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>

    <!-- Summary Cards -->
    <div class="p-4 bg-white rounded-lg shadow my-4">
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <table
        v-else
        class="min-w-full text-sm text-left"
      >
        <thead>
          <tr class="font-semibold">
            <th class="py-2 px-4 text-center">
              Summary
            </th>
            <th />
            <th />
            <th
              class="py-2 px-4 text-center"
              colspan="6"
            >
              Actual
            </th>
          </tr>
          <tr class="border-b">
            <th />
            <th class="px-4 text-center">
              Actual (Ton)
            </th>
            <th class="px-4 text-center">
              Plan (Ton)
            </th>
            <th class="px-4 text-center">
              {{ filters.year }}YTD
            </th>
            <th class="px-4 text-center">
              Target
            </th>
            <th
              v-for="(name, i) in headerMonths"
              :key="i"
              class="px-4 text-center"
            >
              {{ name }}
            </th>
            <th class="px-4 text-center">
              MTD
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in tableData"
            :key="idx"
            class="border-b text-center"
          >
            <td class="">
              {{ row.label }}
            </td>
            <td class="">
              {{ handleValidateAndFormatNumber(row.actual) }}
            </td>
            <td class="">
              {{ handleValidateAndFormatNumber(row.plan) }}
            </td>
            <td class="">
              <div
                class="text-white rounded px-2 py-1 text-center font-bold"
                :style="{ backgroundColor: row.delta < 0 ? '#FF3662' : '#33B57D' }"
              >
                <div>
                  {{ handleValidateAndFormatNumber(row.ytd) }}%
                </div>
                <div
                  class="mt-1 text-xs px-2 py-1 rounded inline-block"
                  :class="row.delta < 0 ? 'bg-pink-100 text-pink-700' : 'bg-green-100 text-green-700'"
                >
                  {{ row.delta < 0 ? '-' : '+' }}{{ Math.abs(row.delta).toFixed(2) }}%
                </div>
              </div>
            </td>
            <td class="">
              {{ handleValidateAndFormatNumber(row.target) }}%
            </td>
            <td
              v-for="(monthVal, m) in row.months"
              :key="m"
              class=""
            >
              {{ handleValidateAndFormatNumber(monthVal) }}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="card p-4 mb-4">
      <h3 class="text-lg font-semibold mb-4">
        Utilization VS Target
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="chartUtilizationVsTargetData"
        :plugins="[backgroundColorPlugin(), drawBarValuePlugin()]"
        :options="chartUtilizationVsTargetOptions"
      />
    </div>
    <div class="card p-4 mb-4">
      <h3 class="text-lg font-semibold mb-4">
        Cargo Capacity Utilization VS Cost Analysis
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="chartUtilizationVsCostData"
        :plugins="[valueLabelPlugin()]"
        :options="chartUtilizationVsCostOptions"
      />
    </div>
    <div class="card p-4 mb-3">
      <h3 class="text-lg font-semibold mb-4">
        Utilization VS POL
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="chartUtilizationVsPolData"
        :plugins="[]"
        :options="chartUtilizationVsPolOptions"
      />
    </div>
    <vs-row
      vs-w="12"
      class="mb-4"
    >
      <vs-col vs-w="6">
        <div class="card p-4 mr-3 h-full">
          <div class="font-semibold">
            Top 5 Loaded Less Than Min. Capacity
          </div>
          <vs-progress
            v-if="isBusy"
            indeterminate
            color="primary"
          >
            primary
          </vs-progress>
          <BarChart
            v-else
            :data="chartRankMinCapacity"
            :plugins="[drawBarValuePlugin(true)]"
            :options="chartRankOptionsMin"
          />
        </div>
      </vs-col>
      <vs-col vs-w="6">
        <div class="card p-4">
          <div class="font-semibold">
            Top 5 Loaded Exceeds Max. Capacity
          </div>
          <vs-progress
            v-if="isBusy"
            indeterminate
            color="primary"
          >
            primary
          </vs-progress>
          <BarChart
            v-else
            :data="chartRankMaxCapacity"
            :plugins="[drawBarValuePlugin(true)]"
            :options="chartRankOptionsMax"
          />
        </div>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import {mapGetters} from "vuex";

import BarChart from "@/components/barChart"
import DatePicker from "@/components/datePicker";
import SelectWithSearch from "@/components/selectWithSearch";
import { PAGE_TITLE } from "@/fixtures/Constants";

export default {
  components: {
    SelectWithSearch,
    DatePicker,
    BarChart
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    const currentYear = this.moment().format('YYYY')
    return {
      isBusy: true,
      usedColors: new Set(),
      filters: {
        owner_id: null,
        route_name: null,
        month: null,
        year: currentYear
      },
      ownerOptions: [],
      routeOptions: [],
      headerMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      tableData: [
        {
          label: 'Actual Vs Plan',
          actual: 0,
          plan: 0,
          target: 0,
          ytd: 0,
          delta: 0,
          months: {
            Jan: 0,
            Feb: 0,
            Mar: 0,
            Apr: 0,
            May: 0,
            MTD: 0
          }
        }
      ],
      chartUtilizationVsTargetData: {
        labels: [],
        datasets: []
      },
      maxUvt: 500,
      chartUtilizationVsCostData: {
        labels: [],
        datasets: []
      },
      maxUvc: 500,
      chartUtilizationVsPolData: {
        labels: [],
        datasets: []
      },
      maxUvp: 500,
      chartRankMinCapacity: {
        labels: [],
        datasets: []
      },
      maxRMinC: 500,
      chartRankMaxCapacity: {
        labels: [],
        datasets: []
      },
      maxRMaxC: 500
    }
  },
  computed: {
    ...mapGetters({
      owners: 'Dashboard/ownerOpt',
      routes: 'Dashboard/routeOpt',
      cargoCapacityUtilizationData: 'CargoCapacityUtilization/cargoCapacityUtilizationData'
    }),
    chartUtilizationVsTargetOptions() {
      return {
        responsive: true,
        layout: { padding: { top: 30 } },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true },
          title: {
            display: false
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            title: { display: true, text: 'Ton' },
            beginAtZero: true,
            grid: { display: true }
          },
          y2: {
            position: 'right',
            beginAtZero: true,
            grid: { display: false },
            title: { display: false },
            ticks: { callback: v => `${v  }%` }
          }
        }
      }
    },
    chartUtilizationVsCostOptions() {
      return {
        responsive: true,
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25
          }
        },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true },
          title: {
            display: false
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            title: { display: false },
            beginAtZero: true,
            grid: { display: true }
          },
          y2: {
            position: 'right',
            grid: { display: false },
            title: { display: false },
            ticks: { callback: v => `${v  }%`, stepSize: 20 }
          }
        }
      }
    },
    chartUtilizationVsPolOptions() {
      return {
        responsive: true,
        layout: { padding: { top: 30 } },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true },
          title: {
            display: false
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            title: { display: true, text: 'Ton' },
            beginAtZero: true,
            grid: { display: true }
          },
          y2: {
            position: 'right',
            beginAtZero: true,
            grid: { display: false },
            title: { display: false },
            ticks: { callback: v => `${v}%` }
          }
        }
      }
    },
    chartRankOptionsMin() {
      return {
        indexAxis: 'y',
        responsive: true,
        layout: { padding: { top: 30 } },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true }
        },
        scales: {
          x: { grid: { display: false }, beginAtZero: true, max: this.maxRMinC },
          y: { grid: { display: false } }
        }
      }
    },
    chartRankOptionsMax() {
      return {
        indexAxis: 'y',
        responsive: true,
        layout: { padding: { top: 30 } },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { enabled: true }
        },
        scales: {
          x: { grid: { display: false }, beginAtZero: true, max: this.maxRMaxC },
          y: { grid: { display: false } }
        }
      }
    }
  },
  watch: {
    owners: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.ownerOptions = val.map(item => ({ text: item.owner_name, value: item.id }))
        }
      }
    },
    routes: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.routeOptions = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    cargoCapacityUtilizationData: {
      handler(val) {
        if (!this.isEmpty(val)) {
          // Populate Data Summary
          const summaryData = val?.cargo_capacity_summary

          this.headerMonths = this.populateDataMonth(
            summaryData?.actual_cargo_month?.length - 1,
            summaryData?.actual_cargo_month?.[0]?.month
          )
          
          this.tableData = [
            {
              label: 'Actual Vs Plan',
              actual: summaryData?.actual_tonnage,
              plan: summaryData?.plan_tonnage,
              target: summaryData?.target_percentage,
              ytd: summaryData?.YTD_percentage,
              delta: summaryData?.YTD_difference,
              months: summaryData?.actual_cargo_month ?? []
            }
          ]

          // Populate Data Utilization Vs Target
          const tempUtilVsTarget = val?.chart?.comparison_with_target
          this.chartUtilizationVsTargetData.labels = [
            ...this.populateDataMonth(
              tempUtilVsTarget?.length - 1,
              tempUtilVsTarget?.[0]?.month
            ),
            'MTD'
          ]
          this.chartUtilizationVsTargetData.datasets = [
            {
              type: 'bar',
              label: 'Plan',
              data: tempUtilVsTarget?.map(v => v?.plan_tonnage),
              backgroundColor: '#fbbf24',
              borderRadius: 8,
              barPercentage: 0.7,
              categoryPercentage: 0.7,
              order: 1
            },
            {
              type: 'bar',
              label: 'Actual',
              data: tempUtilVsTarget?.map(v => v?.actual_tonnage),
              backgroundColor: '#60a5fa',
              barPercentage:0.7,
              categoryPercentage: 0.7,
              borderRadius: 8,
              order: 1
            },
            {
              type: 'line',
              label: '% Utilization',
              data: tempUtilVsTarget?.map(v => v?.utilization),
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 5,
              yAxisID: 'y2',
              order: 0
            }
          ]

          // Populate Data Cargo Capacity Utilization vs Cost Analysis
          const tempUtilVsCost = val?.chart?.comparison_with_cost
          this.chartUtilizationVsCostData.labels = [
            ...this.populateDataMonth(
              tempUtilVsCost?.length - 1,
              tempUtilVsCost?.[0]?.month
            ),
            'MTD'
          ]
          this.chartUtilizationVsCostData.datasets = [
            {
              type: 'bar',
              label: 'Cost Analysis (USD/Ton)',
              data: tempUtilVsCost?.map(v => v?.cost_analysis?.toFixed(2) || 0),
              backgroundColor: 'rgba(168,85,247,0.7)', // purple-500
              barThickness: 40,
              borderRadius: 8,
              order: 1
            },
            {
              type: 'line',
              label: 'Cost Capacity Utilization (%)',
              data: tempUtilVsCost?.map(v => v?.utilization),
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 5,
              yAxisID: 'y2',
              order: 0
            }
          ]

          // Populate Data Utilization VS POL
          const tempUtilVsPol = val?.chart?.comparison_with_pol ?? []
          this.chartUtilizationVsPolData.labels = []
          tempUtilVsPol.forEach(monthData => {
            monthData?.pol.forEach(polData => {
              this.chartUtilizationVsPolData.labels.push(`${this.moment().month(monthData?.month - 1).format('MMM')} - ${polData?.pol}`);
            })
          })
          const uniquePolNames = [...new Set(
            tempUtilVsPol?.flatMap(m => (m?.pol ?? []).map(p => p?.pol)).filter(Boolean)
          )]
          // Generate MTD Labels
          uniquePolNames.forEach(polName => this.chartUtilizationVsPolData.labels.push(`MTD - ${polName}`));
          const polDatasets = [{
            type: 'bar',
            label: 'Avg POL Tonnage',
            data: this.chartUtilizationVsPolData.labels.map(label => {
              const splitter = label.split(' - ')
              const polName = splitter[1]
              const month3Digit = splitter[0]
              let monthNumber = null
              if(month3Digit !== 'MTD') {
                monthNumber = this.moment(month3Digit, 'MMM').month() + 1
              } else {
                monthNumber = this.moment().month() + 1
              }
              const value = tempUtilVsPol.find(x => x.month === monthNumber)?.pol?.find(polData => polData.pol === polName)?.avg_actual_tonnage || 0
              return value
            }),
            backgroundColor: this.chartUtilizationVsPolData.labels.map(x => this.getRandomColor(1)),
            borderRadius: 8,
            barPercentage: 0.7,
            categoryPercentage: 0.7,
            order: 1
          }]
          const polUtilDatasets = [{
            type: 'line',
            label: `% Utilization`,
            data: this.chartUtilizationVsPolData.labels.map(label => {
              const splitter = label.split(' - ')
              const polName = splitter[1]
              const month3Digit = splitter[0]
              let monthNumber = null
              if(month3Digit !== 'MTD') {
                monthNumber = this.moment(month3Digit, 'MMM').month() + 1
              } else {
                monthNumber = this.moment().month() + 1
              }
              const value = tempUtilVsPol.find(x => x.month === monthNumber)?.pol?.find(polData => polData.pol === polName)?.utilization || 0
              return value
            }),
            borderColor: '#ef4444',
            categoryPercentage: 0.7,
            barPercentage: 0.7,
            borderWidth: 2,
            borderDash: [8, 6],
            fill: false,
            pointRadius: 5,
            yAxisID: 'y2',
            order: 0
          }]
          this.chartUtilizationVsPolData.datasets = [
            ...polDatasets,
            ...polUtilDatasets
          ]

          // Populate Data Top 5 Min. Capacity
          const tempRankMin = val?.ranked_min_capacity
          
          this.maxRMinC = Math.max(
            ...tempRankMin?.map(v => v?.actual_tonnage),
            ...tempRankMin?.map(v => v?.plan_tonnage)
          ) + 1000
      
          this.chartRankMinCapacity.labels = tempRankMin?.map(v => [v?.routes, v?.owner_name])
          this.chartRankMinCapacity.datasets = [
            {
              label: 'Actual',
              data: tempRankMin?.map(v => v?.actual_tonnage),
              backgroundColor: '#60a5fa',
              borderRadius: 8,
              categoryPercentage: 0.9,
              barPercentage: 0.9
            },
            {
              label: 'Plan',
              data: tempRankMin?.map(v => v?.plan_tonnage),
              backgroundColor: '#fbbf24',
              borderRadius: 8,
              barPercentage: 0.9,
              categoryPercentage: 0.9
            }
          ]

          // Populate Data Top 5 Max. Capacity
          const tempRankMax = val?.ranked_max_capacity

          this.maxRMaxC = Math.max(
            ...tempRankMax?.map(v => v?.actual_tonnage),
            ...tempRankMax?.map(v => v?.plan_tonnage)
          ) + 1000
          
          this.chartRankMaxCapacity.labels = tempRankMax?.map(v => [v?.routes, v?.owner_name])
          this.chartRankMaxCapacity.datasets = [
            {
              label: 'Actual',
              data: tempRankMax?.map(v => v?.actual_tonnage),
              backgroundColor: '#60a5fa',
              borderRadius: 8,
              categoryPercentage: 0.9,
              barPercentage: 0.9
            },
            {
              label: 'Plan',
              data: tempRankMax?.map(v => v?.plan_tonnage),
              backgroundColor: '#fbbf24 ',
              borderRadius: 8,
              barPercentage: 0.9,
              categoryPercentage: 0.9
            }
          ]
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        if (val.month || val.year) {
          this.fetchOwners()
          this.fetchRoutes()
        }
        this.fetchCargoCapacityUtilization()
      }
    }
  },
  mounted() {
    this.fetchCargoCapacityUtilization()
    this.fetchOwners()
    this.fetchRoutes()
  },
  methods: {
    async fetchCargoCapacityUtilization() {
      this.isBusy = true
      try {
        const apiFilters = Object.fromEntries(
          Object.entries(this.filters || {}).filter(([_, value]) => !!value)
        )
        await this.$store.dispatch('CargoCapacityUtilization/fetchCargoCapacityUtilization', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to get Cargo Capacity Utilization Data, please try again')
      }
    },
    async fetchOwners() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getOwnerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getRouteOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    backgroundColorPlugin() {
      return {
        id: 'customBackground',
        beforeDraw: (chart) => {
          const { ctx, chartArea, scales, data } = chart;
          const yAxis = scales['y2']; // secondary axis (percentage)

          const zones = [
            { from: 0, to: 94.9, color: '#FECCCC' },   // pink
            { from: 95, to: 100, color: '#C7FCCF' }    // green
          ];

          const utilizationPercentageData = data?.datasets?.find(v => v.type === 'line')?.data
          if (utilizationPercentageData.length && utilizationPercentageData?.some(v => v > 100)) {
            const max = Math.max(...utilizationPercentageData);
            zones.push({ from: 100.1, to: max, color: '#FDF4BA' })  // yellow
          }
          
          zones.forEach(zone => {
            const top = yAxis.getPixelForValue(zone.to);
            const bottom = yAxis.getPixelForValue(zone.from);

            ctx.fillStyle = zone.color;
            ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, bottom - top);
          });
        }
      }
    },
    valueLabelPlugin() {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const { ctx, data } = chart;
          ctx.save();
          const meta = chart?.getDatasetMeta(0);
          meta?.data?.forEach((bar, i) => {
            const value = data.datasets[0].data[i];
            ctx.font = 'bold 14px Poppins';
            ctx.fillStyle = '#6d28d9';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(this.handleValidateAndFormatNumber(value), bar.x, bar.y - 6);
          });
          ctx.restore();
        }
      };
    },
    drawBarValuePlugin(isHorizontal = false) {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const ctx = chart.ctx;
          ctx.save();
          chart.data.datasets.forEach((dataset, dsIdx) => {
            const meta = chart.getDatasetMeta(dsIdx);
            meta.data.forEach((bar, i) => {
              const value = dataset.data[i];
              ctx.font = 'semibold 16px Poppins';
              
              const text = this.handleValidateAndFormatNumber(value);
              const textWidth = ctx.measureText(text).width;

              ctx.fillStyle = '#000';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              const textX = isHorizontal ? bar.x + 10 + textWidth / 3 : bar.x;
              const textY = isHorizontal ? bar.y : bar.y - 12;

              if (dataset.type !== 'line' && value > 0)
                ctx.fillText(text, textX, textY);
            });
          });
          ctx.restore();
        }
      };
    },
    populateDataMonth(length, startFrom = 1) {
      if (!length) {
        return []
      }
      return Array.from({ length }, (_, i) =>
        this.moment().month((startFrom - 1 + i) % 12).format('MMM')
      )
    },
    getRandomColor(alpha = 0.7) {
      let color;
      do {
        const r = Math.floor(Math.random() * 156) + 100;
        const g = Math.floor(Math.random() * 156) + 100;
        const b = Math.floor(Math.random() * 156) + 100;
        color = `rgba(${r},${g},${b},${alpha})`;
      } while (this.usedColors.has(color));

      this.usedColors.add(color);
      return color;
    },
    resetFilters() {
      this.filters = {
        owner_id: null,
        route_name: null,
        month: null,
        year: this.moment().format('YYYY')
      }
    }
  }
}
</script>

<style scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.card {
  @apply bg-white rounded-lg
}
</style>
